import {
  createEntityAdapter,
  createSlice,
  isAnyOf,
  PayloadAction,
  Update,
} from '@reduxjs/toolkit';

import * as WidgetApiTypes from '@wix/ambassador-innovation-widget-v1-message/types';

import { senderIsOwner } from '@/utils/sender';

import {
  getMessagesHistory,
  getEditorMockMessages,
  getConversationId,
} from './messagesThunks';
import { Message } from './model';

const MANUAL_INTERVENTION_MAX_AGE_MS = 10 * 60 * 1000;

export const messagesAdapter = createEntityAdapter<Message>({
  sortComparer: (a, b) => {
    if (a.sortKey && b.sortKey) {
      return (a.sortKey ?? '').localeCompare(b.sortKey ?? '');
    }

    if (a.createdDate && b.createdDate) {
      return a.createdDate - b.createdDate;
    }
    return 0;
  },
});

const initialState = messagesAdapter.getInitialState<{
  request: 'idle' | 'loading' | 'loadingHistory' | 'error';
  conversationIdRequest: 'idle' | 'loading';
  pendingMessages: number;
  currentRequestId?: string;
  isTyping: boolean;
  awaitingAnswer: boolean;
  manualInterventionActive: boolean;
  manualInterventionPending: boolean;
  hasUnread: boolean;
  pagingMetadata: WidgetApiTypes.PagingMetadataV2 | null;
  conversationId: string | null;
}>({
  request: 'idle',
  conversationIdRequest: 'idle',
  pendingMessages: 0,
  isTyping: false,
  awaitingAnswer: false,
  manualInterventionActive: false,
  manualInterventionPending: false,
  hasUnread: false,
  pagingMetadata: null,
  conversationId: null,
});

const localAdapterSelectors = messagesAdapter.getSelectors();

function hasRecentOwnerMessage(messages: Message[]) {
  const recentThreshold = Date.now() - MANUAL_INTERVENTION_MAX_AGE_MS;

  return messages.some(
    (message) =>
      senderIsOwner(message.sender) &&
      Boolean(message.createdDate && message.createdDate >= recentThreshold),
  );
}

function syncManualInterventionFromHistory(state: typeof initialState) {
  state.manualInterventionActive = hasRecentOwnerMessage(
    localAdapterSelectors.selectAll(state),
  );
  state.manualInterventionPending = false;
}

export const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    replace: messagesAdapter.setAll,
    addMessage: messagesAdapter.addOne,
    addOptimisticMessage: (state, { payload }: PayloadAction<Message>) => {
      state.pendingMessages++;
      messagesAdapter.addOne(state, payload);
    },
    resetConversation: (state, { payload }: PayloadAction<string>) => {
      state.pendingMessages = 0;
      state.request = 'idle';
      state.currentRequestId = undefined;
      state.isTyping = false;
      state.awaitingAnswer = false;
      state.manualInterventionActive = false;
      state.manualInterventionPending = false;
      state.hasUnread = false;
      state.pagingMetadata = null;
      state.conversationId = payload;
    },
    resetMessages: (
      state,
      {
        payload: { messages, pagingMetadata },
      }: PayloadAction<{
        pagingMetadata?: WidgetApiTypes.PagingMetadataV2;
        messages: Message[];
      }>,
    ) => {
      messagesAdapter.setAll(state, messages);
      state.pagingMetadata = pagingMetadata ?? null;
      syncManualInterventionFromHistory(state);
    },
    addMessages: (
      state,
      {
        payload,
      }: PayloadAction<{
        pagingMetadata?: WidgetApiTypes.PagingMetadataV2;
        messages: Message[];
      }>,
    ) => {
      messagesAdapter.addMany(state, payload.messages);
      state.pagingMetadata = payload.pagingMetadata!;
      syncManualInterventionFromHistory(state);
    },
    updateMessage: (
      state,
      { payload }: PayloadAction<Update<Message, string>>,
    ) => {
      // Message has got an id means it's no longer an optimistic
      if (
        payload.id &&
        payload.changes.id &&
        payload.id !== payload.changes.id &&
        state.pendingMessages > 0 // In case someone uses addMessage instead of addOptimisticMessage
      ) {
        state.pendingMessages--;
      }
      messagesAdapter.updateOne(state, payload);
    },
    addOrUpdateMessage: messagesAdapter.upsertOne,
    removeMessage: messagesAdapter.removeOne,
    setConversationId: (state, { payload }: PayloadAction<string>) => {
      state.conversationId = payload;
    },
    setIsTyping: (
      state,
      { payload }: PayloadAction<boolean | null | undefined>,
    ) => {
      state.isTyping = !!payload;
    },
    setAwaitingAnswer: (
      state,
      { payload }: PayloadAction<boolean | null | undefined>,
    ) => {
      state.awaitingAnswer = !!payload;
    },
    setManualInterventionActive: (
      state,
      { payload }: PayloadAction<boolean | null | undefined>,
    ) => {
      state.manualInterventionActive = !!payload;
    },
    setManualInterventionPending: (
      state,
      { payload }: PayloadAction<boolean | null | undefined>,
    ) => {
      state.manualInterventionPending = !!payload;
    },
    setUnread: (state, { payload }: PayloadAction<boolean>) => {
      state.hasUnread = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getConversationId.pending, (state) => {
      state.conversationIdRequest = 'loading';
    });
    builder.addCase(getConversationId.fulfilled, (state) => {
      state.conversationIdRequest = 'idle';
    });
    builder.addMatcher(
      isAnyOf(getMessagesHistory.pending, getEditorMockMessages.pending),
      (state, action) => {
        const isHistoryRequest = Boolean(
          (action.meta.arg as WidgetApiTypes.ListMessagesRequest)?.cursorPaging
            ?.cursor,
        );
        state.currentRequestId = action.meta.requestId;
        state.request = isHistoryRequest ? 'loadingHistory' : 'loading';
      },
    );
    builder.addMatcher(
      isAnyOf(getMessagesHistory.fulfilled, getEditorMockMessages.fulfilled),
      (state, action) => {
        if (state.currentRequestId === action.meta.requestId) {
          state.currentRequestId = undefined;
          state.request = 'idle';
        }
      },
    );
  },
});

export const { reducer: messagesReducer } = messagesSlice;
